// AvailableSlotSearch.js - Komponent for att soka lediga tider

import React, { useState, useEffect, useContext } from 'react';
import { getAvailableSlots, getAppointmentTypes, getClinicUsers } from './calendarApi';
import { UserContext } from '../../UserContext';
import './Calendar.css';

const AvailableSlotSearch = ({ onSelectSlot, onClose }) => {
  const { user: currentUser } = useContext(UserContext);

  // Filter state - "Alla behandlare" som standard
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Data state
  const [clinicUsers, setClinicUsers] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Sidvis hamtning: scroll till botten hamtar nasta sjok tider
  const PAGE_SIZE = 20;
  const [fetchLimit, setFetchLimit] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Ladda initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [users, types] = await Promise.all([
          getClinicUsers({ calendarOnly: true }),
          getAppointmentTypes()
        ]);

        const filteredUsers = users.filter(u => u.clinic_id === currentUser?.clinic_id);
        setClinicUsers(filteredUsers);
        setAppointmentTypes(types);

        // Satt standard fran-datum till idag
        const today = new Date();
        setFromDate(today.toISOString().split('T')[0]);

        // Satt till-datum till 2 veckor frammat
        const twoWeeks = new Date(today);
        twoWeeks.setDate(twoWeeks.getDate() + 14);
        setToDate(twoWeeks.toISOString().split('T')[0]);
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError('Kunde inte ladda data');
      }
    };

    loadInitialData();
  }, [currentUser]);

  // Sok lediga tider. Backend (GET /api/calendar/available-slots) tar en
  // behandlare i taget - vid "Alla behandlare" sker en sokning per behandlare
  // och resultaten slas ihop sorterat pa starttid. `limit` galler per
  // behandlare OCH den visade listan - samma tal, annars kan en behandlares
  // tidigare tider klippas bort fore ihopslagningen.
  const runSearch = async (limit, isLoadMore = false) => {
    if (!selectedTypeId) {
      setError('Välj bokningstyp');
      return;
    }

    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      setHasSearched(true);

      const options = {
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        limit
      };

      const searchUsers = selectedUserId === 'all'
        ? clinicUsers
        : clinicUsers.filter(u => u.user_id === parseInt(selectedUserId));

      // Vid "Alla behandlare" far en enskild miss inte sanka hela sokningen,
      // men om ALLA anrop misslyckas ska felet visas - inte "Inga lediga tider"
      const settled = await Promise.all(
        searchUsers.map(u =>
          getAvailableSlots(u.user_id, parseInt(selectedTypeId), options)
            .then(r => ({ ok: true, result: r }))
            .catch(err => ({ ok: false, err }))
        )
      );
      const failed = settled.filter(s => !s.ok);
      if (settled.length > 0 && failed.length === settled.length) {
        throw failed[0].err;
      }
      const results = settled.filter(s => s.ok).map(s => s.result);

      // Normalisera: backend svarar {available_slots: [{start, end, ...}],
      // user_id, user_name, duration_minutes}
      const merged = [];
      results.forEach(result => {
        (result.available_slots || []).forEach(slot => {
          merged.push({
            start_time: slot.start,
            end_time: slot.end,
            duration_minutes: result.duration_minutes,
            user_id: result.user_id,
            user_name: result.user_name
          });
        });
      });
      merged.sort((a, b) => a.start_time.localeCompare(b.start_time));

      const shown = merged.slice(0, limit);
      setAvailableSlots(shown);
      // Fylldes sidan finns troligen fler; annars ar intervallet uttomt
      setHasMore(shown.length >= limit);
    } catch (err) {
      console.error('Error searching slots:', err);
      setError(err.response?.data?.error || 'Kunde inte söka lediga tider');
      setAvailableSlots([]);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = () => {
    setFetchLimit(PAGE_SIZE);
    runSearch(PAGE_SIZE);
  };

  // Hamta nasta sjok nar listan scrollats till botten
  const handleLoadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    const next = fetchLimit + PAGE_SIZE;
    setFetchLimit(next);
    runSearch(next, true);
  };

  const handleResultsScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      handleLoadMore();
    }
  };

  // Valj tid och stang - skickar med typ+langd sa bokningsvyn kan forifyllas
  const handleSelectSlot = (slot) => {
    if (onSelectSlot) {
      onSelectSlot({
        start_time: slot.start_time,
        duration_minutes: slot.duration_minutes,
        user_id: slot.user_id,
        appointment_type_id: parseInt(selectedTypeId),
        type_name: selectedType?.name
      });
    }
    if (onClose) {
      onClose();
    }
  };

  // Formatera datum
  const formatSlotDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('sv-SE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Formatera tid
  const formatSlotTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('sv-SE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Gruppera slots per dag
  const groupedSlots = availableSlots.reduce((acc, slot) => {
    const dateKey = new Date(slot.start_time).toDateString();
    if (!acc[dateKey]) {
      acc[dateKey] = {
        date: slot.start_time,
        slots: []
      };
    }
    acc[dateKey].slots.push(slot);
    return acc;
  }, {});

  const selectedType = appointmentTypes.find(t => t.type_id === parseInt(selectedTypeId));
  const selectedUser = clinicUsers.find(u => u.user_id === parseInt(selectedUserId));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content available-slots-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Hitta ledig tid</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && <div className="calendar-error">{error}</div>}

          {/* Sokfilter */}
          <div className="search-filters">
            <div className="filter-row">
              <div className="form-group">
                <label>Behandlare *</label>
                <select
                  value={selectedUserId}
                  onChange={e => setSelectedUserId(e.target.value)}
                >
                  <option value="all">Alla behandlare</option>
                  {clinicUsers.map(user => (
                    <option key={user.user_id} value={user.user_id}>
                      {user.first_name} {user.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Bokningstyp *</label>
                <select
                  value={selectedTypeId}
                  onChange={e => setSelectedTypeId(e.target.value)}
                >
                  <option value="">-- Välj typ --</option>
                  {appointmentTypes.map(type => (
                    <option key={type.type_id} value={type.type_id}>
                      {type.name} ({type.duration_minutes} min)
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="filter-row">
              <div className="form-group">
                <label>Från datum</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Till datum</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                />
              </div>
            </div>

            <button
              className="btn btn-primary search-btn"
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? (
                <>Söker...</>
              ) : (
                <><i className="fas fa-search"></i> Sök lediga tider</>
              )}
            </button>
          </div>

          {/* Resultat */}
          <div className="search-results">
            {loading && (
              <div className="loading-text">Söker lediga tider...</div>
            )}

            {!loading && hasSearched && availableSlots.length === 0 && (
              <div className="no-results">
                <i className="fas fa-calendar-times"></i>
                <p>Inga lediga tider hittades</p>
                <small>Försök med ett annat datumintervall eller annan behandlare</small>
              </div>
            )}

            {!loading && availableSlots.length > 0 && (
              <>
                <div className="results-header">
                  <span className="results-count">
                    {availableSlots.length} lediga tider{hasMore ? ' – scrolla för fler' : ''}
                  </span>
                  {selectedType && (
                    <span className="results-info">
                      {selectedUserId === 'all'
                        ? 'Alla behandlare'
                        : selectedUser && `${selectedUser.first_name} ${selectedUser.last_name}`}
                      {' - '}{selectedType.name}
                    </span>
                  )}
                </div>

                <div className="slots-list" onScroll={handleResultsScroll}>
                  {Object.values(groupedSlots).map((dayGroup, idx) => (
                    <div key={idx} className="day-group">
                      <div className="day-header">
                        <i className="fas fa-calendar-day"></i>
                        {formatSlotDate(dayGroup.date)}
                      </div>
                      <div className="day-slots">
                        {dayGroup.slots.map((slot, slotIdx) => (
                          <button
                            key={slotIdx}
                            className="slot-button"
                            onClick={() => handleSelectSlot(slot)}
                          >
                            <span className="slot-time">
                              {formatSlotTime(slot.start_time)}
                            </span>
                            <span className="slot-duration">
                              {slot.duration_minutes} min
                            </span>
                            {selectedUserId === 'all' && slot.user_name && (
                              <span className="slot-user">
                                {slot.user_name}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Scroll till botten hamtar fler tider inom valt intervall */}
                  {loadingMore && (
                    <div className="slots-list-footer">
                      <i className="fas fa-spinner fa-spin"></i> Hämtar fler tider...
                    </div>
                  )}
                  {!loadingMore && !hasMore && (
                    <div className="slots-list-footer">
                      Inga fler lediga tider i valt datumintervall
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Stäng
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvailableSlotSearch;
